import { useEffect, useState } from "react";
import { TrendingUp, TrendingDown, Minus, Loader2 } from "lucide-react";

/**
 * Sparkline + delta de l'évolution du score IA d'un prospect.
 * Représentation SVG inline minimaliste.
 *
 * Props :
 *   api          : axios instance
 *   prospectId   : id
 *   compact      : boolean — si true, affichage en ligne
 */
export default function ScoreSparkline({ api, prospectId, compact = false, testId = "score-sparkline" }) {
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!prospectId) return;
    const load = async () => {
      try {
        const r = await api.get(`/crm/prospects/${prospectId}/score-history`);
        setHistory(r.data);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [api, prospectId]);

  if (loading) {
    return (
      <div className="text-xs text-slate-500 flex items-center gap-1.5" data-testid={`${testId}-loading`}>
        <Loader2 className="w-3 h-3 animate-spin" /> chargement…
      </div>
    );
  }

  if (!history || !history.items?.length) return null;

  const items = history.items;
  const min = Math.min(...items.map((i) => i.score), history.min_score || 0);
  const max = Math.max(...items.map((i) => i.score), history.max_score || 100);
  const range = Math.max(max - min, 10); // évite division par zéro
  const width = compact ? 80 : 120;
  const height = compact ? 28 : 40;
  const pad = 2;

  // Build polyline points
  const points = items.map((it, i) => {
    const x = pad + (i / Math.max(items.length - 1, 1)) * (width - 2 * pad);
    const y = height - pad - ((it.score - min) / range) * (height - 2 * pad);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");

  const delta = history.delta || 0;
  const tier = items[items.length - 1]?.tier || "warm";
  const stroke = tier === "hot" ? "#f43f5e" : tier === "warm" ? "#f59e0b" : "#0ea5e9";

  const formatDate = (iso) => {
    try { return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" }); }
    catch { return iso?.slice(0, 10) || ""; }
  };

  if (compact) {
    return (
      <div className="inline-flex items-center gap-2" data-testid={testId}>
        <svg width={width} height={height} className="flex-shrink-0">
          <polyline points={points} fill="none" stroke={stroke} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          {items.map((it, i) => {
            const x = pad + (i / Math.max(items.length - 1, 1)) * (width - 2 * pad);
            const y = height - pad - ((it.score - min) / range) * (height - 2 * pad);
            return <circle key={i} cx={x} cy={y} r="1.8" fill={stroke} />;
          })}
        </svg>
        <DeltaBadge delta={delta} />
      </div>
    );
  }

  return (
    <div className="bg-[#0b0811] rounded-xl border border-white/5 p-4" data-testid={testId}>
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Évolution du score IA</div>
          <div className="text-xs text-slate-400 mt-0.5">{items.length} évaluation{items.length > 1 ? "s" : ""}</div>
        </div>
        <DeltaBadge delta={delta} large />
      </div>
      <svg width="100%" height="80" viewBox={`0 0 ${width} ${height + 20}`} preserveAspectRatio="none" className="overflow-visible">
        {/* Grid lines */}
        <line x1="0" y1={height - pad} x2={width} y2={height - pad} stroke="rgba(255,255,255,0.05)" strokeWidth="0.5" />
        <line x1="0" y1={pad} x2={width} y2={pad} stroke="rgba(255,255,255,0.05)" strokeWidth="0.5" />
        {/* Polyline */}
        <polyline points={points} fill="none" stroke={stroke} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        {/* Area fill */}
        <polygon
          points={`${pad},${height - pad} ${points} ${width - pad},${height - pad}`}
          fill={stroke}
          opacity="0.1"
        />
        {/* Points */}
        {items.map((it, i) => {
          const x = pad + (i / Math.max(items.length - 1, 1)) * (width - 2 * pad);
          const y = height - pad - ((it.score - min) / range) * (height - 2 * pad);
          return (
            <g key={i}>
              <circle cx={x} cy={y} r="2.5" fill={stroke}>
                <title>{it.score}/100 — {formatDate(it.at)}</title>
              </circle>
              {i === items.length - 1 && (
                <text x={x} y={y - 4} fontSize="6" fill="#fff" textAnchor="middle" fontWeight="bold">{it.score}</text>
              )}
            </g>
          );
        })}
      </svg>
      <div className="flex items-center justify-between text-[10px] text-slate-500 mt-1">
        <span>{formatDate(items[0].at)}</span>
        <span className="text-slate-400 font-medium">min {history.min_score} · max {history.max_score}</span>
        <span>{formatDate(items[items.length - 1].at)}</span>
      </div>
    </div>
  );
}

const DeltaBadge = ({ delta, large = false }) => {
  if (delta === 0) {
    return (
      <span className={`inline-flex items-center gap-1 ${large ? "text-sm font-bold" : "text-xs"} text-slate-400`}>
        <Minus className={large ? "w-4 h-4" : "w-3 h-3"} /> stable
      </span>
    );
  }
  const positive = delta > 0;
  const Icon = positive ? TrendingUp : TrendingDown;
  return (
    <span className={`inline-flex items-center gap-1 ${large ? "text-sm font-bold" : "text-xs font-semibold"} ${positive ? "text-emerald-400" : "text-rose-400"}`}>
      <Icon className={large ? "w-4 h-4" : "w-3 h-3"} />
      {positive ? "+" : ""}{delta} pt{Math.abs(delta) > 1 ? "s" : ""}
    </span>
  );
};
